// Offline-recording sync-queue bridge (APP-6665, US2 / T042).
//
// Thin IPC wrapper over the Rust sync orchestrator's queue-level pause/resume
// (FR-023) and explicit retry. These are desktop-only — on web / non-Tauri they
// no-op so callers can invoke unconditionally. The Rust source of truth is
// `src-tauri/src/offline_recording/sync/orchestrator.rs`; the commands are bare
// main-app commands (no plugin prefix) registered in `src-tauri/src/lib.rs`.

import { isTauri } from '@/tauri/utils';

const SYNC_PAUSE_COMMAND = 'offline_sync_pause';
const SYNC_RESUME_COMMAND = 'offline_sync_resume';
const SYNC_RETRY_COMMAND = 'offline_sync_retry';
const SYNC_AUTOMATIC_READINESS_COMMAND = 'offline_sync_automatic_readiness';
const SYNC_AUTOMATIC_REDRIVE_COMMAND = 'offline_sync_redrive_automatic';

export interface OfflineSyncAutomaticRedriveReadiness {
  targetGeneration: number;
}

export type OfflineSyncAutomaticRedriveOutcome =
  | 'accepted'
  | 'skipped_runtime'
  | 'skipped_missing_target'
  | 'skipped_stale_target_generation'
  | 'skipped_connectivity_not_ready'
  | 'skipped_user_mismatch'
  | 'skipped_queue_paused'
  | 'skipped_capture_active';

export const offlineSyncBridge = {
  /**
   * Pause the offline sync queue: new drains are suppressed until {@link resume}.
   * In-flight recordings the engine already owns still finish. No-op off-desktop.
   */
  async pause(): Promise<void> {
    if (!isTauri()) {
      return;
    }
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke<void>(SYNC_PAUSE_COMMAND);
  },

  /**
   * Resume the offline sync queue. The actual re-drain is driven by the Rust
   * connectivity trigger / boot re-enqueue; this only clears the pause flag.
   * No-op off-desktop.
   */
  async resume(): Promise<void> {
    if (!isTauri()) {
      return;
    }
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke<void>(SYNC_RESUME_COMMAND);
  },

  /**
   * Resume and retry the offline sync queue now. If Rust already knows the
   * backend is reachable, this drains immediately instead of waiting for a new
   * connectivity state transition. No-op off-desktop.
   */
  async retry(): Promise<void> {
    if (!isTauri()) {
      return;
    }
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke<void>(SYNC_RETRY_COMMAND);
  },

  /**
   * Snapshot the active generation-correlated connectivity target before an
   * asynchronous engine startup. Null means automatic re-drive is not eligible.
   */
  async getAutomaticRedriveReadiness(): Promise<OfflineSyncAutomaticRedriveReadiness | null> {
    if (!isTauri()) {
      return null;
    }
    const { invoke } = await import('@tauri-apps/api/core');
    return invoke<OfflineSyncAutomaticRedriveReadiness | null>(
      SYNC_AUTOMATIC_READINESS_COMMAND
    );
  },

  /**
   * Request a fail-closed automatic re-drive. This is intentionally distinct
   * from manual Retry and cannot resume a user-paused queue.
   */
  async redriveAutomatically(
    expectedUserId: string,
    expectedTargetGeneration: number
  ): Promise<OfflineSyncAutomaticRedriveOutcome> {
    if (!isTauri()) {
      return 'skipped_runtime';
    }
    const { invoke } = await import('@tauri-apps/api/core');
    return invoke<OfflineSyncAutomaticRedriveOutcome>(SYNC_AUTOMATIC_REDRIVE_COMMAND, {
      expectedUserId,
      expectedTargetGeneration,
    });
  },
};
